import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'CommonComponent';
import { EditTimeModal } from './component';
import AccessRights from "utils/AccessRights";
import { getWeeklyTimesheetById, weeklyTimesheetRedirectToList, createOrUpdateWeeklyTimesheet } from 'actions';

const DetailList = ({handleFilter, taskFilterEnabled = false, taskFilterValue = ""}) => {
    const dispatch = useDispatch();
    const weeklyTimesheetState = useSelector(state => state.weeklyTimesheetState);
    const { data, headers, loading } = useSelector(state => state.timesheetReportsState.monthlyDetailed);
    const session = useSelector((s) => s.session);
    
    const object_viewed_id = session?.current_page_permissions?.object_id || "";

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState({});
    const [rowData, setRowData] = useState([]);

    useEffect(() => {
        if (weeklyTimesheetState.showUpdateForm) { 
            setShowEditModal(true);
        }
        else{ 
            handleEditClose();
        }
    }, [weeklyTimesheetState.showUpdateForm]);

    useEffect(() => {
        if (weeklyTimesheetState.redirectToList) { 
        }
    }, [weeklyTimesheetState.redirectToList]);

    const getRowId = (row = {}, index = 0) => {
        const id = row?.weekly_timesheet_id ?? row?.id;
        if (id !== undefined && id !== null && String(id).trim() !== "") {
            return String(id);
        }

        return [
            row?.program_name,
            row?.category_task,
            row?.week_ending,
            row?.employee,
            row?.hour_type,
            index,
        ].join("|");
    };

    const isTotalRow = (row = {}) => String(row?.program_name || "").toLowerCase() === "total";
    const isSelectedTotalRow = (row = {}) => Boolean(row?.__isSelectedTotalRow);

    const filteredData = useMemo(() => {
        const baseRows = (Array.isArray(data) ? data : []).filter((row) => !isSelectedTotalRow(row));

        if (!taskFilterEnabled || !taskFilterValue) {
            return taskFilterEnabled ? baseRows.filter((row) => !isTotalRow(row)) : baseRows;
        }

        return baseRows.filter((row) => {
            if (isTotalRow(row)) return false;
            return String(row?.category_task || row?.task || "") === taskFilterValue;
        });
    }, [data, taskFilterValue, taskFilterEnabled]);

    useEffect(() => {
        if (!taskFilterEnabled) {
            setSelectedRowIds({});
            return;
        }

        const allSelected = filteredData.reduce((acc, row, rowIndex) => {
            if (isTotalRow(row)) return acc;
            const id = getRowId(row, rowIndex);
            acc[id] = true;
            return acc;
        }, {});

        setSelectedRowIds(allSelected);
    }, [taskFilterEnabled, taskFilterValue, filteredData]);

    const selectableIds = useMemo(
        () => filteredData
            .map((row, index) => ({ row, id: getRowId(row, index) }))
            .filter(({ row }) => !isTotalRow(row) && !isSelectedTotalRow(row))
            .map(({ id }) => id),
        [filteredData]
    );

    const selectedSelectableCount = selectableIds.filter((id) => selectedRowIds[id]).length;
    const allSelectableChecked = selectableIds.length > 0 && selectedSelectableCount === selectableIds.length;

    const parseNumeric = (value) => {
        if (typeof value === 'number') return value;
        const parsed = Number(String(value ?? '').replace(/,/g, ''));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const selectedTotal = useMemo(() => {
        return filteredData.reduce((acc, row, index) => {
            const id = getRowId(row, index);
            if (!selectedRowIds[id] || isTotalRow(row) || isSelectedTotalRow(row)) {
                return acc;
            }

            return acc + parseNumeric(row?.weekly_total);
        }, 0);
    }, [filteredData, selectedRowIds]);

    const dynamicTotalMap = useMemo(() => {
        const ignoredKeys = new Set([
            'program_name',
            'category_task',
            'comment',
            'week_ending',
            'employee',
            'hour_type',
            'weekly_total',
        ]);

        const keys = Object.keys(headers || {}).filter((key) => !ignoredKeys.has(key));

        return keys.reduce((acc, key) => {
            const value = filteredData.reduce((sum, row, rowIndex) => {
                const id = getRowId(row, rowIndex);
                if (!selectedRowIds[id] || isTotalRow(row) || isSelectedTotalRow(row)) {
                    return sum;
                }

                return sum + parseNumeric(row?.[key]);
            }, 0);

            acc[key] = value;
            return acc;
        }, {});
    }, [headers, filteredData, selectedRowIds]);

    const selectedSummaryRow = useMemo(() => {
        if (!taskFilterEnabled) return null;

        return {
            __isSelectedTotalRow: true,
            program_name: `Total (${selectedSelectableCount})`,
            category_task: "",
            week_ending: "",
            employee: "",
            hour_type: "",
            ...dynamicTotalMap,
            weekly_total: selectedTotal,
            comment: "",
        };
    }, [taskFilterEnabled, selectedSelectableCount, dynamicTotalMap, selectedTotal]);

    const tableData = useMemo(() => {
        if (!taskFilterEnabled || !selectedSummaryRow) {
            return filteredData;
        }

        return [selectedSummaryRow, ...filteredData];
    }, [filteredData, selectedSummaryRow, taskFilterEnabled]);

    const toggleRowSelection = (row, index) => {
        const id = getRowId(row, index);
        if (isTotalRow(row) || isSelectedTotalRow(row)) return;

        setSelectedRowIds((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleCheckAll = () => {
        const next = {};
        selectableIds.forEach((id) => {
            next[id] = true;
        });
        setSelectedRowIds(next);
    };

    const handleUncheckAll = () => {
        setSelectedRowIds({});
    };

    const handleEditClose = () => {
        setShowEditModal(false);
        setRowData([]);
        dispatch(weeklyTimesheetRedirectToList());
    };

    const handleEditModalShow = (row) => {
        setRowData(row);
        dispatch(getWeeklyTimesheetById(row.weekly_timesheet_id, 'update', object_viewed_id));
    };

    const handleUpdate = (dataToUpdate) => {
        dispatch(createOrUpdateWeeklyTimesheet(dataToUpdate, 'PUT', object_viewed_id))
        .then(() => {
            handleEditClose();
            handleFilter();
        })
        .catch(() => {
            // Error is already handled by the action
        });
    };

    const formatValue = (value) => {
        if (typeof value === 'number') {
            return <span>{value.toFixed(2)}</span>;
        }
        return value;
    };

    const generateColumns = (headers) => {
        const selectionColumn = taskFilterEnabled
            ? [{
                id: "row_select",
                header: (
                    <input
                        type="checkbox"
                        checked={allSelectableChecked}
                        onChange={(event) => {
                            if (event.target.checked) {
                                handleCheckAll();
                                return;
                            }

                            handleUncheckAll();
                        }}
                    />
                ),
                accessor: (_row) => "",
                sortable: false,
                searchable: false,
                cell: (_value, row, rowIndex) => {
                    const rowId = getRowId(row, rowIndex);
                    const disabled = isTotalRow(row) || isSelectedTotalRow(row);

                    return (
                        <input
                            type="checkbox"
                            checked={Boolean(selectedRowIds[rowId])}
                            disabled={disabled}
                            onChange={() => toggleRowSelection(row, rowIndex)}
                        />
                    );
                },
            }]
            : [];

        const staticColumns = [
            {
                id: "program_name",
                header: "Program",
                accessor: (row) => row.program_name,
                sortable: !taskFilterEnabled,
                searchable: true,
            },
            {
                id: "category_task",
                header: "Category / Task",
                accessor: (row) => row.category_task,
                sortable: !taskFilterEnabled,
                searchable: true,
            },
            {
                id: "week_ending",
                header: "Week Ending",
                accessor: (row) => row.week_ending,
                sortable: !taskFilterEnabled,
                searchable: true,
            },
            {
                id: "employee",
                header: "Employee",
                accessor: (row) => row.employee,
                sortable: !taskFilterEnabled,
                searchable: true,
            },
            {
                id: "hour_type",
                header: "Hours",
                accessor: (row) => row.hour_type,
                sortable: false,
                searchable: false,
            },
        ];

        const dynamicColumns = Object.entries(headers).map(([key, header]) => {
            if (!['program_name', 'category_task', 'comment', 'week_ending', 'employee', 'hour_type', 'weekly_total'].includes(key)){
                return {
                    id: key,
                    header: `(${header.toUpperCase()})`,
                    accessor: (row) => row[key],
                    cell: (value) => formatValue(value),
                    sortable: !taskFilterEnabled,
                    searchable: false,
                }
            }
            return null;
        }).filter(Boolean);

        const actionColumns = [
            
            {
                id: "weekly_total",
                header: "Weekly Hours",
                accessor: (row) => row.weekly_total,
                cell: (value) => formatValue(value)
            },
            {
                id: "comment",
                header: "Comment",
                accessor: (row) => row.comment,
                cell: (value) => formatValue(value)
            },
            ...(!taskFilterEnabled ? [{
                id: 'action',
                header: 'Action',
                accessor: (row) => row,
                cell: (row) => (
                    <span>
                        {((AccessRights(
                            session.current_page_permissions,
                            "edit",
                            row.created_by
                        ) &&
                        ((row.is_deleted === "n" || (1==1)) && row.program_name !== "Total"))) && (
                            <img
                            src="/assets/images/edit-icon.svg"
                            alt="Edit"
                            onClick={() => handleEditModalShow(row)}
                            style={{ cursor: "pointer", marginRight: "10px" }}
                            />
                        )}
                        
                    </span>
                ),
            }] : []),
        ];

        return [...selectionColumn, ...staticColumns, ...dynamicColumns, ...actionColumns];
    };

    const columns = useMemo(() => generateColumns(headers || {}), [
        headers,
        taskFilterEnabled,
        allSelectableChecked,
        selectedRowIds,
    ]);

    return (
        <div className="details-table-container">
            {taskFilterEnabled && (
                <div className="d-flex align-items-end flex-wrap gap-3 mb-3">
                    <button type="button" className="n-u-btn" onClick={handleCheckAll}>Check All ({selectableIds.length})</button>
                    <button type="button" className="c-b-btn" onClick={handleUncheckAll}>Uncheck All</button>
                </div>
            )}

            <DataTable
                columns={columns}
                data={tableData}
                serverSide={false}
                defaultPageSize={taskFilterEnabled ? 250 : 50}
                showSearch={false}
                loading={loading}
            />

            {showEditModal &&  rowData && 
            (<EditTimeModal
                show={showEditModal}
                rowData={rowData}
                handleClose={handleEditClose}
                onUpdate={handleUpdate}
            />)}

        </div>
    );
};

export default DetailList;